import PropTypes from 'prop-types'
import React from 'react'

import { Trash } from 'lucide-react'
import { Badge, cn, ButtonIcon } from 'twyne-new.ui'

import Collapsible from '@/modules/offer/views/form/components/fields/Collapsible'

import Dropzone from './Dropzone'
import { externalThreshold, internalThreshold, itemThreshold } from './thresholdConfig'

const CardPropTypes = {
  path: PropTypes.string,
  id: PropTypes.number,
  head: PropTypes.object,
  handleSetTree: PropTypes.func,
  drag: PropTypes.object,
  externalDropzone: PropTypes.object,
  itemDropzone: PropTypes.object,
  internalDropzone: PropTypes.object,
  parentMultipleChildren: PropTypes.bool,
  canHaveManyChildren: PropTypes.bool,
}

export default function Card({ path, id, head, handleSetTree, drag, externalDropzone, itemDropzone, internalDropzone, parentMultipleChildren, canHaveManyChildren, children }) {
  const { title, icon: Icon, subtitle } = head
  const { dragReference, isDragging } = drag
  const { externalDropReference, externalIsOver } = externalDropzone
  const { itemDropReference, itemIsOver } = itemDropzone
  const { internalDropReference, internalIsOver, internalCanDrop } = internalDropzone

  const badge = subtitle ? subtitle : false

  const handleDelete = () => {
    const action = 'delete'
    const from = path
    const fromPosition = id

    handleSetTree(action, { from, fromPosition })
  }

  return <>
    <Collapsible
      ref={dragReference}
      contentClass={cn(!parentMultipleChildren && 'max-h-120 overflow-hidden', internalCanDrop && !internalIsOver ? 'twy-bg-base-neutral' : 'twy-bg-base-accent', 'pt-0 p-4 pr-0! relative')}
      className={cn(parentMultipleChildren && 'rounded-r-none border-r-0', itemIsOver && 'opacity-50 border-dashed border-2 twy-border-base-accent', 'relative mt-2')}
      title={<Title
        dragReference={dragReference}
        title={title}
        icon={Icon}
        badge={badge}
        onDelete={handleDelete}
        parentMultipleChildren={parentMultipleChildren}
        externalDropReference={externalDropReference}
        externalIsOver={externalIsOver}
        path={path}
        id={id}
        isDragging={isDragging}
        itemDropReference={itemDropReference}
        itemIsOver={itemIsOver}
      />}
    >
      <div className="max-h-120 overflow-y-scroll pr-[8px] -mr-[12px]">
        {children}
        <DropContent
          canHaveManyChildren={canHaveManyChildren}
          isDragging={isDragging}
          path={path}
          id={id}
          internalDropReference={internalDropReference}
          internalIsOver={internalIsOver}
          hasChildren={!!children}
        />
      </div>
    </Collapsible>
  </>
}

const Title = ({ title, icon: Icon, badge, onDelete, parentMultipleChildren, externalDropReference, externalIsOver, path, id, isDragging, itemDropReference, itemIsOver }) => <>
  <div className='py-2 w-full flex justify-between items-center'>
    <DropBetweenItems
      parentMultipleChildren={parentMultipleChildren}
      externalDropReference={externalDropReference}
      externalIsOver={externalIsOver}
      path={path}
      id={id}
      isDragging={isDragging}
    />
    <DropHeader
      path={path}
      id={id}
      itemDropReference={itemDropReference}
      itemIsOver={itemIsOver}
      isDragging={isDragging}
    />
    <div className='flex items-center gap-2'>
      <Icon size={16} />
      <span className='capitalize'>{title?.replaceAll('-', ' ')}</span>
      {badge ? <Badge color='accent' size='sm'>{badge}</Badge> : null}
    </div>
    <ButtonIcon onClick={onDelete}>
      <Trash size={14} />
    </ButtonIcon>
  </div>
</>

const DropBetweenItems = ({ parentMultipleChildren, externalDropReference, externalIsOver, path, id, isDragging }) => <>
  {!isDragging && !parentMultipleChildren ? <>
    <Dropzone
      path={path}
      id={id}
      dropReference={externalDropReference}
      isOver={externalIsOver}
      thresholdConfig={externalThreshold}
    />
  </> : null}
</>

const DropHeader = ({ path, id, itemDropReference, itemIsOver, isDragging }) => <>
  {!isDragging ? <>
    <Dropzone
      path={path}
      id={id}
      dropReference={itemDropReference}
      isOver={itemIsOver}
      thresholdConfig={itemThreshold}
      feedback={false}
    />
  </> : null}
</>

const DropContent = ({ hasChildren, canHaveManyChildren, isDragging, path, id, internalDropReference, internalIsOver }) => <>
  {(!hasChildren || canHaveManyChildren) && !isDragging ? <>
    <Dropzone
      path={`${path}-${id}`}
      id={0}
      dropReference={internalDropReference}
      isOver={internalIsOver}
      thresholdConfig={internalThreshold}
    />
  </> : null}
</>

Card.propTypes = CardPropTypes
